import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import RefundInvoiceModal from "../../components/modals/RefundInvoiceModal";
import CollectPaymentModal from "../../components/modals/CollectPaymentModal";
import InvoiceDetailsModal from "../../components/modals/InvoiceDetailsModal";
import { showSuccessToast, showErrorToast } from "../../utils/notificationUtils";
import { invoiceAPI, handleApiError } from "../../services/api";
import { pdfServices } from "../../services/pdfservices";
import { USER_TYPES } from "../../constants/userTypes";
import { useAuth } from "../../hooks/useAuth";
import "../../styles/billing.css";

const FILTERS = [
  { key: "all", param: undefined },
  { key: "walkin", param: 1 },
  { key: "visit", param: 0 },
];

const Billing = ({ clinicData, isOwner }) => {
  const { t, i18n } = useTranslation();
  const { getUserType } = useAuth();

  const userType = typeof getUserType === "function" ? getUserType() : null;
  const canManageBilling = userType === USER_TYPES.CLINIC_MODERATOR || !!isOwner;

  const clinicId = clinicData?.clinic?.id;
  const clinicCurrency = clinicData?.clinic?.currency || "USD";
  const locale = i18n.language === "ar" ? "ar-EG" : "en-US";
  const dateLocale = i18n.language === "ar" ? "ar-SA" : "en-US";

  const [invoices, setInvoices] = useState({ items: [], total: 0, lastPage: 1 });
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [printingId, setPrintingId] = useState(null);
  const [collectTarget, setCollectTarget] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchInvoices = async (targetPage = 1, filterKey = filter) => {
    if (!clinicId) return;
    setLoading(true);
    try {
      const filterDef = FILTERS.find((f) => f.key === filterKey) || FILTERS[0];
      const response = await invoiceAPI.getInvoices({
        clinic_id: clinicId,
        per_page: 50,
        page: targetPage,
        ...(filterDef.param !== undefined ? { is_pos: filterDef.param } : {}),
      });
      const payload = response?.data?.data;
      const items = Array.isArray(payload) ? payload : payload?.items || payload?.data || [];
      const pagination = Array.isArray(payload) ? {} : payload?.pagination || {};
      setInvoices({
        items,
        total: pagination.total ?? items.length,
        lastPage: pagination.last_page ?? 1,
      });
    } catch (e) {
      console.warn("Failed to fetch invoices:", e);
      setInvoices({ items: [], total: 0, lastPage: 1 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices(1, filter);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, filter]);

  const formatMoney = (value) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString(dateLocale, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (_) {
      return "-";
    }
  };

  const num = (v) => parseFloat(v) || 0;

  const statusPill = (inv) => {
    if (inv.refunded_at) {
      return { className: "billing-pill--gray", label: t("billing.status.refunded", "Refunded") };
    }
    if (num(inv.balance) <= 0) {
      return { className: "billing-pill--teal", label: t("billing.status.paid", "Paid") };
    }
    if (num(inv.paid) > 0) {
      return { className: "billing-pill--amber", label: t("billing.status.partial", "Partial") };
    }
    return { className: "billing-pill--red", label: t("billing.status.unpaid", "Unpaid") };
  };

  const buyerLabel = (inv) => {
    const buyer = inv.buyer_name || inv.buyer_phone || inv.client?.name || "";
    return {
      text: buyer || t("billing.walkInFallback", "Walk-in"),
      isFallback: !buyer,
    };
  };

  const downloadBlob = (blob, defaultFileName) => {
    const disposition = blob.headers?.["content-disposition"];
    let fileName = defaultFileName;
    if (disposition) {
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match) fileName = match[1];
    }
    const blobUrl = URL.createObjectURL(blob.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  const handlePrint = async (inv) => {
    if (!inv?.id || printingId) return;
    setPrintingId(inv.id);
    try {
      const response = await pdfServices.generatePosInvoicePdf(inv.id);
      downloadBlob(response, `invoice-${inv.invoice_number || inv.id}.pdf`);
    } catch (e) {
      console.warn("Failed to print invoice:", e);
      showErrorToast(t("billing.printFailed", "Failed to generate the PDF. Please try again."));
    } finally {
      setPrintingId(null);
    }
  };

  const handleCollect = async (data) => {
    if (!collectTarget) return;
    setActionLoading(true);
    try {
      await invoiceAPI.collectPayment(collectTarget.id, data);
      showSuccessToast(
        t("billing.collectSuccess", "Payment recorded for {{number}}", {
          number: collectTarget.invoice_number || `#${collectTarget.id}`,
        })
      );
      setCollectTarget(null);
      fetchInvoices(page, filter);
    } catch (err) {
      showErrorToast(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefund = async (reason) => {
    if (!refundTarget) return;
    setActionLoading(true);
    try {
      await invoiceAPI.refundInvoice(refundTarget.id, { reason });
      showSuccessToast(
        t("billing.refundSuccess", "Refund issued for {{number}}", {
          number: refundTarget.invoice_number || `#${refundTarget.id}`,
        })
      );
      setRefundTarget(null);
      fetchInvoices(page, filter);
    } catch (err) {
      showErrorToast(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  // KPI strip from the current page (pagination window) of the fetched list
  const kpi = (() => {
    let outstanding = 0;
    let unpaid = 0;
    let paidFull = 0;
    invoices.items.forEach((inv) => {
      if (inv.refunded_at) return;
      const balance = num(inv.balance);
      if (balance > 0) {
        outstanding += balance;
        unpaid += 1;
      } else {
        paidFull += 1;
      }
    });
    return { outstanding, unpaid, paidFull };
  })();

  const emptyText = {
    all: t("billing.emptyAll", "No invoices yet"),
    walkin: t("billing.emptyWalkin", "No walk-in invoices yet"),
    visit: t("billing.emptyVisit", "No visit invoices yet"),
  };

  const loadingDots = (
    <span className="billing-dots">
      <span className="billing-dot"></span>
      <span className="billing-dot"></span>
      <span className="billing-dot"></span>
    </span>
  );

  return (
    <div className="billing" dir={i18n.dir()} lang={i18n.language}>
      {/* ===== Header ===== */}
      <div className="billing-head">
        <div>
          <h1>{t("billing.title", "Billing")}</h1>
          <div className="billing-sub">
            {t("billing.subtitle", "Invoices, payments and refunds for {{clinicName}}", {
              clinicName:
                clinicData?.clinic?.name_en || clinicData?.clinic?.name || t("billing.clinicFallback", "your clinic"),
            })}
          </div>
        </div>
      </div>

      {!canManageBilling ? (
        <div className="billing-card">
          <div className="billing-empty">
            <div className="billing-empty-icon">🔒</div>
            <div>{t("billing.noPermission", "Only the clinic owner or moderator can manage billing.")}</div>
          </div>
        </div>
      ) : (
        <>
          {/* ===== KPI strip ===== */}
          <div className="billing-kpi-row">
            <div className="billing-kpi">
              <div className="billing-kpi-badge billing-kpi-badge--red">💰</div>
              <div className="billing-kpi-meta">
                <div className="billing-kpi-label">{t("billing.kpi.outstanding", "Outstanding")}</div>
                <div className="billing-kpi-value">
                  {loading ? loadingDots : `${formatMoney(kpi.outstanding)} ${clinicCurrency}`}
                </div>
                <div className="billing-kpi-foot">{t("billing.kpi.outstandingFoot", "Open balances")}</div>
              </div>
            </div>
            <div className="billing-kpi">
              <div className="billing-kpi-badge billing-kpi-badge--amber">🧾</div>
              <div className="billing-kpi-meta">
                <div className="billing-kpi-label">{t("billing.kpi.unpaid", "Unpaid")}</div>
                <div className="billing-kpi-value">{loading ? loadingDots : kpi.unpaid}</div>
                <div className="billing-kpi-foot">{t("billing.kpi.unpaidFoot", "Invoices with a balance")}</div>
              </div>
            </div>
            <div className="billing-kpi">
              <div className="billing-kpi-badge billing-kpi-badge--teal">✅</div>
              <div className="billing-kpi-meta">
                <div className="billing-kpi-label">{t("billing.kpi.paid", "Paid in full")}</div>
                <div className="billing-kpi-value">{loading ? loadingDots : kpi.paidFull}</div>
                <div className="billing-kpi-foot">{t("billing.kpi.paidFoot", "Settled invoices")}</div>
              </div>
            </div>
          </div>

          {/* ===== Filter chips ===== */}
          <div className="billing-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                className={`billing-chip${filter === f.key ? " billing-chip--active" : ""}`}
                onClick={() => setFilter(f.key)}
              >
                {t(`billing.filter.${f.key}`, f.key === "all" ? "All" : f.key === "walkin" ? "Walk-in" : "Visit")}
              </button>
            ))}
          </div>

          {/* ===== Invoices table ===== */}
          <div className="billing-card">
            {loading ? (
              <div className="billing-empty">{loadingDots}</div>
            ) : invoices.items.length === 0 ? (
              <div className="billing-empty">
                <div>{emptyText[filter]}</div>
                {filter === "walkin" && (
                  <div className="billing-empty-sub">
                    {t("billing.emptyWalkinHint", "Create one with the New Walk-In Invoice button")}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="billing-table-head" dir={i18n.dir()}>
                  <span>{t("billing.colInvoice", "Invoice #")}</span>
                  <span>{t("billing.colSource", "Source")}</span>
                  <span>{t("billing.colBuyer", "Buyer / Client")}</span>
                  <span>{t("billing.colDate", "Issued")}</span>
                  <span className="billing-num">{t("billing.colTotal", "Total")}</span>
                  <span className="billing-num">{t("billing.colPaid", "Paid")}</span>
                  <span className="billing-num">{t("billing.colBalance", "Balance")}</span>
                  <span>{t("billing.colStatus", "Status")}</span>
                  <span></span>
                </div>
                {invoices.items.map((inv) => {
                  const pill = statusPill(inv);
                  const buyer = buyerLabel(inv);
                  const balance = num(inv.balance);
                  const paid = num(inv.paid);
                  const total = num(inv.total);
                  const refunded = !!inv.refunded_at;
                  return (
                    <div className="billing-row" dir={i18n.dir()} key={inv.id}>
                      <button
                        type="button"
                        className="billing-number billing-number--link"
                        onClick={() => setDetailId(inv.id)}
                        title={t("billing.details.view", "View invoice details")}
                      >
                        {inv.invoice_number || `#${inv.id}`}
                      </button>
                      <span>
                        <span className={`billing-pill ${inv.is_pos ? "billing-pill--teal" : "billing-pill--gray"}`}>
                          {inv.is_pos
                            ? t("billing.sourceWalkin", "Walk-in")
                            : t("billing.sourceVisit", "Visit")}
                        </span>
                      </span>
                      <span className={buyer.isFallback ? "billing-buyer billing-buyer--fallback" : "billing-buyer"}>
                        {buyer.text}
                      </span>
                      <span className="billing-muted">{formatDate(inv.issued_at)}</span>
                      <span className="billing-num" data-label={t("billing.colTotal", "Total")}>
                        {formatMoney(total)}
                      </span>
                      <span className="billing-num" data-label={t("billing.colPaid", "Paid")}>
                        {formatMoney(paid)}
                      </span>
                      <span
                        className={`billing-num ${balance > 0 ? "billing-balance" : ""}`}
                        data-label={t("billing.colBalance", "Balance")}
                      >
                        {formatMoney(balance)}
                      </span>
                      <span>
                        <span className={`billing-pill ${pill.className}`}>{pill.label}</span>
                      </span>
                      <span className="billing-actions">
                        {!refunded && balance > 0 && (
                          <button className="billing-action-btn billing-action-btn--collect" onClick={() => setCollectTarget(inv)}>
                            {t("billing.collectAction", "Collect")}
                          </button>
                        )}
                        {!refunded && balance <= 0 && (
                          <button className="billing-action-btn billing-action-btn--refund" onClick={() => setRefundTarget(inv)}>
                            {t("billing.refundAction", "Refund")}
                          </button>
                        )}
                        <button
                          className="billing-action-btn"
                          onClick={() => handlePrint(inv)}
                          disabled={printingId === inv.id}
                        >
                          {printingId === inv.id
                            ? loadingDots
                            : `🖨 ${t("billing.print", "Print")}`}
                        </button>
                      </span>
                    </div>
                  );
                })}
                {invoices.lastPage > 1 && (
                  <div className="billing-pagination">
                    <button
                      className="billing-page-btn"
                      onClick={() => {
                        const p = Math.max(1, page - 1);
                        setPage(p);
                        fetchInvoices(p, filter);
                      }}
                      disabled={page <= 1 || loading}
                    >
                      {t("billing.prev", "Prev")}
                    </button>
                    <span className="billing-page-info">
                      {t("billing.pageInfo", "{{page}} / {{total}}", {
                        page,
                        total: invoices.lastPage,
                      })}
                    </span>
                    <button
                      className="billing-page-btn"
                      onClick={() => {
                        const p = Math.min(invoices.lastPage, page + 1);
                        setPage(p);
                        fetchInvoices(p, filter);
                      }}
                      disabled={page >= invoices.lastPage || loading}
                    >
                      {t("billing.next", "Next")}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      <InvoiceDetailsModal
        isOpen={!!detailId}
        onClose={() => setDetailId(null)}
        invoiceId={detailId}
        currency={clinicData?.clinic?.currency || "JOD"}
      />

      <CollectPaymentModal
        isOpen={!!collectTarget}
        onClose={() => setCollectTarget(null)}
        onConfirm={handleCollect}
        isLoading={actionLoading}
        invoice={collectTarget}
      />

      <RefundInvoiceModal
        isOpen={!!refundTarget}
        onClose={() => setRefundTarget(null)}
        onConfirm={handleRefund}
        isLoading={actionLoading}
        invoice={refundTarget}
      />
    </div>
  );
};

export default Billing;
